<template>
  <div class="article-detail-page" v-loading="loading">
    <!-- Article Hero -->
    <div class="article-hero">
      <div class="article-hero-inner">
        <div class="article-cover">
          <img v-if="article.cover" :src="article.cover" :alt="article.title" />
          <div v-else class="cover-placeholder">
            <i class="el-icon-document"></i>
          </div>
        </div>
        <div class="article-info">
          <p class="article-title">{{ article.title }}</p>
          <div class="meta-grid">
            <div class="meta-item" v-if="article.topic">
              <span class="meta-label">{{ $t("articleDetail.researchTopic") }}</span>
              <span class="meta-value">{{ article.topic }}</span>
            </div>
            <div class="meta-item" v-if="article.journal">
              <span class="meta-label">{{ $t("articleDetail.journal") }}</span>
              <span class="meta-value">{{ article.journal }}</span>
            </div>
            <div class="meta-item" v-if="article.author">
              <span class="meta-label">{{ $t("articleDetail.author") }}</span>
              <span class="meta-value">{{ article.author }}</span>
            </div>
            <div class="meta-item" v-if="article.publishDate">
              <span class="meta-label">{{ $t("articleDetail.publishDate") }}</span>
              <span class="meta-value">{{ article.publishDate }}</span>
            </div>
            <div class="meta-item" v-if="article.pageViews != null">
              <span class="meta-label">{{ $t("articleDetail.pageViews") }}</span>
              <span class="meta-value">
                {{ Number(article.pageViews).toLocaleString() }}{{ $t("articleDetail.viewsSuffix") }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Article Content -->
    <div class="article-content-wrapper">
      <div class="article-inner">
        <!-- Abstract -->
        <section class="content-section" v-if="article.abstract">
          <p class="section-label">{{ $t("articleDetail.abstract") }}</p>
          <p class="abstract-text">{{ article.abstract }}</p>
        </section>

        <!-- Download -->
        <div class="download-section" >
          <el-button
            type="primary"
            round
            icon="el-icon-download"
            @click="onDownload"
          >
            {{ $t("articleDetail.downloadPDF") }}
          </el-button>
        </div>

        <!-- Related Articles -->
        <section class="content-section" v-if="relatedArticles.length">
          <p class="section-label">{{ $t("articleDetail.relatedArticles") }}</p>
          <div
            v-for="(ref, index) in relatedArticles"
            :key="ref.id"
            class="reference-item"
            @click="goToArticle(ref.id)"
          >
            <span class="ref-index">[{{ index + 1 }}]</span>
            <span class="ref-text">{{ ref.title }}</span>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import { getImage } from "@/utils/tools";
import {
  getArticleDetail,
  addReport,
  addDownloadReport,
  listRelatedArticle,
  getArticlePdfUrl,
} from "@/api/article";

export default {
  name: "ArticleDetailPage",
  data() {
    return {
      loading: false,
      article: {},
      relatedArticles: [],
    };
  },
  watch: {
    "$route.params.id": {
      immediate: true,
      handler(id) {
        if (id != null) this.loadArticle(id);
      },
    },
  },
  methods: {
    async loadArticle(id) {
      this.loading = true;
      try {
        const [detail, related] = await Promise.all([
          getArticleDetail(id),
          addReport({ articleId: id, articleName: this.$route.params.title, clickTimes:"1"}), 
          listRelatedArticle(id),
        ]);
        const data = detail?.data || {};
        this.article = {
          id: data.articleId,
          title: data.articleName,
          topic: data.topic,
          journal: data.typeName,
          author: data.researcher,
          publishDate: data.year,
          pageViews: data.pageViews,
          cover: data.articleCoverAddr ? getImage(data.articleCoverAddr) : null,
          abstract: data.summary || data.synopsis,
          fullTextAddr: data.fullTextAddr || "",
        };
        const list = Array.isArray(related?.data) ? related.data : [];
        this.relatedArticles = list.map((item) => ({
          id: item.articleId,
          title: item.articleName,
        }));
      } catch (err) {
        this.article = {};
        this.relatedArticles = [];
      } finally {
        this.loading = false;
      }
    },
    onDownload() {
      const url = getArticlePdfUrl(this.article.fullTextAddr);
      addDownloadReport({ articleId: this.article.id, articleName: this.article.title, pageViews:"1"});
      if (url) window.open(url, "_blank");
    },
    goToArticle(id) {
      if (id != null && id !== this.article.id) {
        this.$router.push({ path: `/article/${id}` });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.article-detail-page {
  flex: 1;
}

/* ===== Hero ===== */
.article-hero {
  background: $bg-orange-light;
  padding: 40px 0;
}

.article-hero-inner {
  max-width: $max-width;
  margin: 0 auto;
  padding: 0 24px;
  display: flex;
  gap: 36px;
}

.article-cover {
  flex-shrink: 0;
  width: 250px;

  img {
    width: 100%;
    height: 350px;
    object-fit: cover;
    border-radius: 4px;
    box-shadow: 2px 4px 12px rgba(0, 0, 0, 0.1);
    display: block;
  }

  .cover-placeholder {
    width: 100%;
    height: 350px;
    background: linear-gradient(135deg, #f0e6d6, #e8d5c0);
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 2px 4px 12px rgba(0, 0, 0, 0.1);

    i {
      font-size: 40px;
      color: $primary-orange;
      opacity: 0.5;
    }
  }
}

.article-info {
  flex: 1;
  min-width: 0;

  .article-title {
    font-size: 24px;
    color: $title-color;
    margin-bottom: 40px;
  }
}

.meta-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.meta-item {
  display: flex;
  align-items: baseline;
  margin-bottom: 3px;

  .meta-label {
    font-size: 16px;
    color: $text-dark;
    min-width: 80px;
    flex-shrink: 0;
  }

  .meta-value {
    font-size: 16px;
    color: $text-gray;
  }
}

/* ===== Content ===== */
.article-content-wrapper {
  padding: 32px 0 48px;
}

.article-inner {
  max-width: $max-width;
  margin: 0 auto;
  padding: 0 24px;
}

.content-section {
  margin-bottom: 28px;

  .section-label {
    font-size: 24px;
    color: $text-dark;
    margin-bottom: 20px;
  }
}

.abstract-text {
  font-size: 16px;
  line-height: 1.8;
  color: $text-gray;
}

.keyword-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  .el-tag {
    border-color: $border-color;
    color: $text-gray;
  }
}

.download-section {
  margin: 24px 0;
}

.reference-item {
  font-size: 16px;
  color: $text-gray;
  line-height: 1.8;
  margin-bottom: 6px;
  cursor: pointer;
  transition: color 0.2s;

  &:hover {
    color: $primary-orange;

    .ref-text {
      text-decoration: underline;
    }
  }

  .ref-index {
    color: $text-light;
    margin-right: 6px;
  }
}
</style>
